import React, { useState, useRef, useEffect, useCallback, memo } from "react";
import { ArrowRight, Sparkles, Check } from "lucide-react";
import { Box, Typography, useMediaQuery } from "@mui/material";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";

const HeroSection = memo(function HeroSection() {
  const [currentAction, setCurrentAction] = useState(
    "Ready to interact - Try clicking inside the app!",
  );
  const [actionTimestamp, setActionTimestamp] = useState(Date.now());
  const [showInteractiveApp, setShowInteractiveApp] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [eventQueue, setEventQueue] = useState([]);
  const [isAnimating, setIsAnimating] = useState(false);

  const isBelow991 = useMediaQuery("(max-width:991px)");

  const heroSectionRef = useRef(null);
  const videoRef = useRef(null);
  const iframeRef = useRef(null);
  const isSectionVisible = useRef(false);
  const timeoutRef = useRef(null);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const navigate = useNavigate();

  // Stop all media activity when section leaves viewport
  const stopAllActivity = useCallback(() => {
    if (videoRef.current) {
      videoRef.current.pause();
    }
    if (iframeRef.current && showInteractiveApp) {
      const src = iframeRef.current.src;
      iframeRef.current.src = "about:blank";
      iframeRef.current.dataset.src = src;
    }
    setEventQueue([]);
    setIsAnimating(false);
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, [showInteractiveApp]);

  // Resume activity when section enters viewport
  const resumeActivity = useCallback(() => {
    if (videoRef.current) {
      videoRef.current.play().catch(() => {});
    }
    if (iframeRef.current && iframeRef.current.dataset.src) {
      iframeRef.current.src = iframeRef.current.dataset.src;
      delete iframeRef.current.dataset.src;
    }
  }, []);

  // IntersectionObserver on hero section to detect mobile scroll in/out
  useEffect(() => {
    const section = heroSectionRef.current;
    if (!section) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const wasVisible = isSectionVisible.current;
          isSectionVisible.current = entry.isIntersecting;

          if (!entry.isIntersecting && wasVisible) {
            stopAllActivity();
          } else if (entry.isIntersecting && !wasVisible) {
            resumeActivity();
            // Trigger video load when section becomes visible
            if (!videoLoaded) {
              setVideoLoaded(true);
            }
          }
        });
      },
      {
        threshold: 0.1,
        rootMargin: '50px', // Start loading slightly before visible
      },
    );

    observer.observe(section);
    return () => {
      observer.disconnect();
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [stopAllActivity, resumeActivity, videoLoaded]);

  // Listen for messages from the mobile app component
  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data.type === "USER_ACTION") {
        if (showInteractiveApp && isSectionVisible.current) {
          const newAction = event.data.action;
          setEventQueue((prev) => [...prev, newAction]);
        }
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [showInteractiveApp]);

  // Process event queue with animation
  useEffect(() => {
    if (
      eventQueue.length > 0 &&
      !isAnimating &&
      showInteractiveApp &&
      isSectionVisible.current
    ) {
      setIsAnimating(true);
      const nextEvent = eventQueue[0];
      setCurrentAction(nextEvent);
      setActionTimestamp(Date.now());
      setEventQueue((prev) => prev.slice(1));

      timeoutRef.current = setTimeout(() => {
        setIsAnimating(false);
      }, 3000);
    }

    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [eventQueue, isAnimating, showInteractiveApp]);

  const handleTryInteractive = () => {
    setShowInteractiveApp(true);
    setCurrentAction("Loading interactive app...");
    setActionTimestamp(Date.now());
    setEventQueue([]);
    setIsAnimating(false);
  };

  return (
    <section
      className="pt-10 md:pt-10 pb-12 md:pb-16 px-4 md:px-6"
      ref={heroSectionRef}
    >
      <div className="container mx-auto max-w-full">
        <div className="text-center space-y-4 md:space-y-6">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#E5B978]/10 border border-[#E5B978]/30 text-[#E5B978] text-sm font-medium">
            <Sparkles className="w-4 h-4" />
            Voice-First AI Platform
          </div>

          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold leading-tight text-balance">
            Speak Once
            <span className="bg-gradient-to-r from-[#E5B978] via-[#F5D9A3] to-[#E5B978] bg-clip-text text-transparent">
              {" "}Create Everything.
            </span>
          </h1>

          <Typography
            sx={{
              textAlign: "center",
              fontFamily: "Inter",
              fontSize: isBelow991 ? "16px" : "24px",
              fontStyle: "normal",
              fontWeight: 500,
              lineHeight: "normal",
              letterSpacing: "0.1px",
              opacity: 0.8,
              background:
                "linear-gradient(90deg, rgba(255, 255, 255, 0.80) 0%, rgba(153, 153, 153, 0.80) 100%)",
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              margin: "0 auto",
              marginTop: "30px",
            }}
          >
            Generate stunning videos, professional images, and complete websites
            with simple voice commands.
          </Typography>

          {/* Hero Visual - Split Layout */}
          <div className="pt-4 md:pt-6 relative w-full">
            <div className="grid lg:grid-cols-2 gap-6 md:gap-8 lg:gap-20 items-start relative w-full max-w-full">
              {/* Vertical Divider Line */}
              <div className="hidden lg:block absolute left-1/2 -translate-x-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-transparent via-[#E5B978] to-transparent shadow-lg shadow-[#E5B978]/50" />

              {/* Left Side - Mobile Mockup */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1, delay: 0.3 }}
                className="flex justify-center lg:justify-end lg:pr-12 w-full"
              >
                <div className="relative">
                  {/* Phone Frame */}
                  <div
                    className="relative w-[300px] h-[560px] sm:w-[340px] sm:h-[630px] md:w-[380px] md:h-[700px] bg-gradient-to-b from-[#1A1B2E] to-[#0F0F1E] rounded-[3rem] border-8 border-[#E5B978]/20 shadow-2xl shadow-[#E5B978]/30"
                    onMouseEnter={() => setIsHovering(true)}
                    onMouseLeave={() => setIsHovering(false)}
                  >
                    {/* Notch */}
                    <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 md:w-40 h-6 md:h-7 bg-[#0F0F1E] rounded-b-3xl z-10" />

                    {/* Screen Content */}
                    <div className="absolute inset-4 bg-gradient-to-br from-[#252640] to-[#1A1B2E] rounded-[2.5rem] overflow-hidden">
                      {!showInteractiveApp ? (
                        <>
                          {/* Video Demo */}
                          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#E5B978]/20 to-[#1A1B2E]">
                            {videoLoaded ? (
                              <video
                                ref={videoRef}
                                src="/videos/app-demo-video-latest.mp4"
                                className="w-full h-full object-cover"
                                autoPlay
                                loop
                                muted
                                playsInline
                                preload="metadata"
                              />
                            ) : (
                              <div className="w-full h-full bg-gradient-to-br from-[#E5B978]/10 to-[#1A1B2E] flex items-center justify-center">
                                <Sparkles className="w-16 h-16 text-[#E5B978] animate-pulse" />
                              </div>
                            )}
                          </div>

                          {/* Hover / Tap Overlay with Try Yourself Button */}
                          {isHovering && (
                            <motion.div
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              exit={{ opacity: 0 }}
                              className="absolute inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-20"
                            >
                              <Box
                                onClick={handleTryInteractive}
                                style={{
                                  display: "flex",
                                  flexDirection: "row",
                                  justifyContent: "center",
                                  alignItems: "center",
                                  padding: "12px 16px",
                                  gap: "8px",
                                  alignSelf: "center",
                                  borderRadius: "8px",
                                  border: "1px solid rgba(255, 255, 255, 0.20)",
                                  background: "#F6BE6B",
                                  boxShadow:
                                    "0px 0px 24px 0px rgba(253, 215, 157, 0.60)",
                                  color: "#17182C",
                                  textAlign: "center",
                                  fontFamily: "Inter",
                                  fontSize: "16px",
                                  fontStyle: "normal",
                                  fontWeight: 600,
                                  lineHeight: "20px",
                                  letterSpacing: "0.1px",
                                  maxWidth: isBelow991 ? "350px" : "386px",
                                  cursor: "pointer",
                                  transition: "background 0.3s ease",
                                }}
                                onMouseEnter={(e) => e.currentTarget.style.background = "#E5AD5A"}
                                onMouseLeave={(e) => e.currentTarget.style.background = "#F6BE6B"}
                              >
                                <span>Try Yourself</span>
                                <ArrowRight style={{ width: "20px", height: "20px", marginLeft: "8px" }} />
                              </Box>
                            </motion.div>
                          )}
                        </>
                      ) : (
                        <iframe
                          ref={iframeRef}
                          src="https://v0-mobile-demo-app-for-landing.vercel.app/"
                          className="w-full h-full border-0"
                          title="Mobile App Demo"
                          sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-pointer-lock"
                          loading="lazy"
                        />
                      )}
                    </div>

                    {/* Phone Buttons */}
                    <div className="absolute right-0 top-32 w-1 h-16 bg-[#E5B978]/30 rounded-l" />
                    <div className="absolute right-0 top-52 w-1 h-12 bg-[#E5B978]/30 rounded-l" />
                  </div>

                  {/* Tap hint for mobile */}
                  {!showInteractiveApp && (
                    <button
                      className="md:hidden absolute inset-4 rounded-[2.5rem] z-20 flex items-center justify-center"
                      onClick={handleTryInteractive}
                      aria-label="Try the interactive app"
                    >
                      <span className="bg-black/50 backdrop-blur-sm text-white text-sm font-medium px-4 py-2 rounded-full border border-white/20">
                        Tap to Try
                      </span>
                    </button>
                  )}

                  {/* Glow Effect */}
                  <div className="absolute inset-0 bg-[#E5B978]/20 blur-3xl rounded-full -z-10" />
                </div>
              </motion.div>

              {/* Right Side - Descriptive Text */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 1, delay: 0.5 }}
                className="space-y-4 md:space-y-5 text-left lg:pl-12 w-full max-w-full"
              >
                <div className="inline-flex items-center gap-2 px-4 md:px-6 py-2 rounded-full bg-[#E5B978]/10 border border-[#E5B978]/30">
                  <Sparkles className="w-4 h-4 text-[#E5B978]" />
                  <span className="text-sm text-[#E5B978] font-medium">
                    Live Demo
                  </span>
                </div>

                <h3 className="text-2xl sm:text-3xl md:text-4xl font-bold leading-tight text-balance">
                  Watch Your Voice
                  <span className="block text-[#E5B978]">
                    Transform Into Reality
                  </span>
                </h3>

                <div className="space-y-2 md:space-y-3 text-sm md:text-base text-white/70">
                  <p className="flex items-start gap-2 md:gap-3">
                    <span className="text-[#E5B978] font-bold text-lg md:text-xl">
                      1.
                    </span>
                    <span>
                      <strong className="text-white">Speak naturally</strong> -
                      Just say what you want to create, no technical jargon
                      needed
                    </span>
                  </p>
                  <p className="flex items-start gap-2 md:gap-3">
                    <span className="text-[#E5B978] font-bold text-lg md:text-xl">
                      2.
                    </span>
                    <span>
                      <strong className="text-white">AI understands</strong> -
                      Our advanced AI processes your voice and intent instantly
                    </span>
                  </p>
                  <p className="flex items-start gap-2 md:gap-3">
                    <span className="text-[#E5B978] font-bold text-lg md:text-xl">
                      3.
                    </span>
                    <span>
                      <strong className="text-white">Content ready</strong> -
                      Professional videos, images, or websites generated in
                      seconds
                    </span>
                  </p>
                </div>

                {/* Live Action Display */}
                <AnimatePresence mode="wait">
                  <motion.div
                    key={actionTimestamp}
                    initial={{ opacity: 0, x: -20, scale: 0.95 }}
                    animate={{ opacity: 1, x: 0, scale: 1 }}
                    exit={{ opacity: 0, x: 20, scale: 0.95 }}
                    transition={{
                      duration: 0.6,
                      ease: [0.25, 0.1, 0.25, 1],
                    }}
                    className="mt-3 md:mt-4 p-3 md:p-4 bg-[#E5B978]/10 border border-[#E5B978]/30 rounded-xl"
                  >
                    <div className="flex items-start gap-2 md:gap-3">
                      <div className="w-2 h-2 bg-[#E5B978] rounded-full animate-pulse flex-shrink-0 mt-1.5" />
                      <div>
                        <p className="text-xs text-[#E5B978] font-semibold mb-1">
                          {showInteractiveApp ? "LIVE EVENT" : "READY TO START"}
                        </p>
                        <p className="text-xs md:text-sm text-white">
                          {currentAction}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                </AnimatePresence>

                <div className="pt-2 md:pt-3 flex flex-wrap items-center gap-2 md:gap-3 text-xs md:text-sm text-white/60">
                  <div className="flex items-center gap-2">
                    <Check className="w-5 h-5 text-green-500" />
                    <span>No editing required</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className="w-5 h-5 text-green-500" />
                    <span>Export ready</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Check className="w-5 h-5 text-green-500" />
                    <span>Share instantly</span>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>

          <div className="pt-10 md:pt-12 lg:pt-16 w-full max-w-full mx-auto text-center">
            <p className="text-base md:text-lg lg:text-xl text-white/70 max-w-full mx-auto leading-relaxed px-4">
              Let AI agents handle your client calls, schedule appointments, and
              follow-ups automatically. Transform your business communication and
              content creation with the power of your voice. No technical skills
              required—just speak, and watch your ideas come to life instantly.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 md:gap-4 pt-2 md:pt-4">
              <Box
                onClick={() => navigate("/workspace")}
                style={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "center",
                  alignItems: "center",
                  padding: "12px 16px",
                  gap: "8px",
                  alignSelf: "stretch",
                  borderRadius: "8px",
                  border: "1px solid rgba(255, 255, 255, 0.20)",
                  background: "#F6BE6B",
                  boxShadow: "0px 0px 24px 0px rgba(253, 215, 157, 0.60)",
                  color: "#17182C",
                  textAlign: "center",
                  fontFamily: "Inter",
                  fontSize: "16px",
                  fontStyle: "normal",
                  fontWeight: 600,
                  lineHeight: "20px",
                  letterSpacing: "0.1px",
                  cursor: "pointer",
                  transition: "background 0.3s ease",
                  width: "100%",
                  maxWidth: "386px",
                }}
                sx={{
                  "@media (min-width: 640px)": {
                    width: "auto",
                  },
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = "#E5AD5A"}
                onMouseLeave={(e) => e.currentTarget.style.background = "#F6BE6B"}
              >
                <span>Start Creating Now</span>
                <ArrowRight style={{ width: "20px", height: "20px", marginLeft: "8px" }} />
              </Box>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
});

export default HeroSection;
